import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import camelCaseKeys from 'camelcase-keys'

export const useNotificationsStore = defineStore('notifications', () => {
  const mentorMatchingNotification = reactive({
    isVisible: false,
    matchingUrl: null,
  })
  const notifications = ref([])
  const unreadCount = ref(0)
  const unseenCount = ref(0)
  const hasNextPage = ref(false)
  const userFeedClient = ref(null)

  const addNotification = (notification) => {
    const newNotifications = camelCaseKeys(notification.results, { deep: true })
    unseenCount.value += 1
    unreadCount.value += 1
    notifications.value = [...newNotifications, ...notifications.value]

    const notificationsToShow = [...newNotifications]
      .map(({ activities }) => activities)
      .flat()
      .filter((activity) => activity.action === 'matching_success')
    mentorMatchingNotification.isVisible = notificationsToShow.length > 0
    mentorMatchingNotification.matchingUrl = notificationsToShow[0]?.url
  }

  const initGetStreamClient = async ({
    streamApiKey,
    streamAppId,
    userGetStreamToken,
    userId,
    subscribeSuccessCallback,
    subscribeErrorCallback,
  }) => {
    // eslint-disable-next-line no-undef
    const client = stream.connect(streamApiKey, userGetStreamToken, streamAppId)

    userFeedClient.value = client.feed('notification', userId)

    try {
      const { results, next, unread, unseen } = await userFeedClient.value.get()
      unreadCount.value = unread
      unseenCount.value = unseen
      hasNextPage.value = !!next
      notifications.value = camelCaseKeys(results, {
        deep: true,
      })
    } catch (error) {
      console.error(error)
    }

    // Subscribe to new notifications
    try {
      await userFeedClient.value.subscribe(async (notification) => {
        // In order to keep state updated, we have to send another request to get activity group id (without that we only get some part of this object)
        const activityGroup = await userFeedClient.value.getActivityDetail(
          notification.id,
        )
        addNotification(activityGroup)
      })
      subscribeSuccessCallback()
    } catch (_) {
      subscribeErrorCallback()
    }
  }

  const loadNextPage = async () => {
    try {
      const { next, results } = await userFeedClient.value.get({
        offset: notifications.value.length,
      })
      hasNextPage.value = !!next
      notifications.value = [
        ...notifications.value,
        ...camelCaseKeys(results, { deep: true }),
      ]
    } catch (error) {
      console.error(error)
    }
  }

  const markAllNotificationsAsSeen = async () => {
    await userFeedClient.value.get({
      mark_seen: true,
    })
    notifications.value = notifications.value.map((notification) => ({
      ...notification,
      isSeen: true,
    }))
    unseenCount.value = 0
  }

  const markAllNotificationsAsRead = async () => {
    await userFeedClient.value.get({
      mark_seen: true,
      mark_read: true,
    })
    notifications.value = notifications.value.map((notification) => ({
      ...notification,
      isRead: true,
      isSeen: true,
    }))
    unreadCount.value = 0
    unseenCount.value = 0
  }

  const markNotificationAsRead = async (notificationId) => {
    await userFeedClient.value.get({
      mark_seen: [notificationId],
      mark_read: [notificationId],
    })
    notifications.value = notifications.value.map((notification) => {
      if (notification.id === notificationId) {
        notification.isRead = true
        notification.isSeen = true
      }
      return notification
    })
    if (unreadCount.value >= 1) unreadCount.value -= 1
    if (unseenCount.value >= 1) unseenCount.value -= 1
  }

  return {
    mentorMatchingNotification,
    notifications,
    unreadCount,
    unseenCount,
    hasNextPage,
    initGetStreamClient,
    loadNextPage,
    markAllNotificationsAsSeen,
    markAllNotificationsAsRead,
    markNotificationAsRead,
  }
})
